import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Dropdown from '@/Components/Dropdown/Dropdown';
import Icon from '@/Components/Icon/Icon';
import HorizontalSeparator from '@/Components/Shared/HorizontalSeparator';
import Switch from '@/Components/Switch/Switch';
import { EditorFontSize, EditorLineHeight, EditorLineWidth, LocalPrefKey } from '@standardnotes/snjs';
import { useCallback, useMemo } from 'react';
import { SuperName, jtString } from '@standardnotes/features';
import { c } from 'ttag';
import { Subtitle, Title, Text } from '../../PreferencesComponents/Content';
import PreferencesGroup from '../../PreferencesComponents/PreferencesGroup';
import PreferencesSegment from '../../PreferencesComponents/PreferencesSegment';
import { CHANGE_EDITOR_WIDTH_COMMAND } from '@standardnotes/ui-services';
import { useLocalPreference } from '../../../../Hooks/usePreference';
function getEditorFontSizeLabel(fontSize) {
    switch (fontSize) {
        case EditorFontSize.ExtraSmall:
            return c('B6.Preferences.Appearance.Label').t `Extra small`;
        case EditorFontSize.Small:
            return c('B6.Preferences.Appearance.Label').t `Small`;
        case EditorFontSize.Normal:
            return c('B6.Preferences.Appearance.Label').t `Normal`;
        case EditorFontSize.Medium:
            return c('B6.Preferences.Appearance.Label').t `Medium`;
        case EditorFontSize.Large:
            return c('B6.Preferences.Appearance.Label').t `Large`;
    }
}
function getEditorLineHeightLabel(lineHeight) {
    switch (lineHeight) {
        case EditorLineHeight.None:
            return c('B6.Preferences.Appearance.Label').t `None`;
        case EditorLineHeight.Tight:
            return c('B6.Preferences.Appearance.Label').t `Tight`;
        case EditorLineHeight.Snug:
            return c('B6.Preferences.Appearance.Label').t `Snug`;
        case EditorLineHeight.Normal:
            return c('B6.Preferences.Appearance.Label').t `Normal`;
        case EditorLineHeight.Relaxed:
            return c('B6.Preferences.Appearance.Label').t `Relaxed`;
        case EditorLineHeight.Loose:
            return c('B6.Preferences.Appearance.Label').t `Loose`;
    }
}
function getEditorLineWidthLabel(width) {
    switch (width) {
        case EditorLineWidth.Narrow:
            return c('B4.Notes.EditorOptions.Label').t `Narrow`;
        case EditorLineWidth.Wide:
            return c('B4.Notes.EditorOptions.Label').t `Wide`;
        case EditorLineWidth.Dynamic:
            return c('B4.Notes.EditorOptions.Label').t `Dynamic`;
        case EditorLineWidth.FullWidth:
            return c('B4.Notes.EditorOptions.Label').t `Full width`;
    }
}
const EditorDefaults = ({ application }) => {
    const [lineHeight, setLineHeight] = useLocalPreference(LocalPrefKey.EditorLineHeight);
    const handleLineHeightChange = (value) => {
        setLineHeight(value);
    };
    const lineHeightDropdownOptions = useMemo(() => Object.values(EditorLineHeight).map((lineHeight) => ({
        label: getEditorLineHeightLabel(lineHeight),
        value: lineHeight,
    })), []);
    const [monospaceFont, setMonospaceFont] = useLocalPreference(LocalPrefKey.EditorMonospaceEnabled);
    const toggleMonospaceFont = () => {
        setMonospaceFont(!monospaceFont);
    };
    const [fontSize, setFontSize] = useLocalPreference(LocalPrefKey.EditorFontSize);
    const handleFontSizeChange = (value) => {
        setFontSize(value);
    };
    const fontSizeDropdownOptions = useMemo(() => Object.values(EditorFontSize).map((fontSize) => ({
        label: getEditorFontSizeLabel(fontSize),
        value: fontSize,
    })), []);
    const [editorWidth] = useLocalPreference(LocalPrefKey.EditorLineWidth);
    const toggleEditorWidthModal = useCallback(() => {
        application.keyboardService.triggerCommand(CHANGE_EDITOR_WIDTH_COMMAND, true);
    }, [application.keyboardService]);
    return (_jsx(PreferencesGroup, { children: _jsxs(PreferencesSegment, { children: [_jsx(Title, { children: c('B6.Preferences.Appearance.Title').t `Editor` }), _jsxs("div", { className: "mt-2", children: [_jsxs("div", { className: "flex justify-between gap-2 md:items-center", children: [_jsxs("div", { className: "flex flex-col", children: [_jsx(Subtitle, { children: c('B6.Preferences.Appearance.Subtitle').t `Monospace Font` }), _jsx(Text, { children: jtString(c('B6.Preferences.Appearance.Info').jt `Toggles the font style in plaintext and ${SuperName} notes`) })] }), _jsx(Switch, { onChange: toggleMonospaceFont, checked: monospaceFont })] }), _jsx(HorizontalSeparator, { classes: "my-4" }), _jsxs("div", { children: [_jsx(Subtitle, { children: c('B6.Preferences.Appearance.Subtitle').t `Font size` }), _jsx(Text, { children: jtString(c('B6.Preferences.Appearance.Action').jt `Sets the font size in plaintext and ${SuperName} notes`) }), _jsx("div", { className: "mt-2", children: _jsx(Dropdown, { label: c('B6.Preferences.Appearance.Action').t `Select the font size for plaintext notes`, items: fontSizeDropdownOptions, value: fontSize, onChange: handleFontSizeChange }) })] }), _jsx(HorizontalSeparator, { classes: "my-4" }), _jsxs("div", { children: [_jsx(Subtitle, { children: c('B6.Preferences.Appearance.Subtitle').t `Line height` }), _jsx(Text, { children: jtString(c('B6.Preferences.Appearance.Action')
                                        .jt `Sets the line height (leading) in plaintext and ${SuperName} notes`) }), _jsx("div", { className: "mt-2", children: _jsx(Dropdown, { label: c('B6.Preferences.Appearance.Action').t `Select the line height for plaintext notes`, items: lineHeightDropdownOptions, value: lineHeight, onChange: handleLineHeightChange }) })] }), _jsx(HorizontalSeparator, { classes: "my-4" }), _jsxs("div", { children: [_jsx(Subtitle, { children: c('B6.Preferences.Appearance.Subtitle').t `Editor width` }), _jsx(Text, { children: c('B6.Preferences.Appearance.Action').t `Sets the max editor width for all notes` }), _jsx("div", { className: "mt-2", children: _jsxs("button", { className: "flex w-full min-w-55 items-center justify-between rounded border border-border bg-default px-3.5 py-1.5 text-left text-base text-foreground md:w-fit lg:text-sm", onClick: toggleEditorWidthModal, children: [getEditorLineWidthLabel(editorWidth), _jsx(Icon, { type: "chevron-down", size: "normal" })] }) })] })] })] }) }));
};
export default EditorDefaults;
